/**
 * Session Manager Utility
 * Manages per-tab session identifiers with lifecycle management
 */

/**
 * Generates a UUID v4 using the Crypto API
 * @returns {string} A UUID v4 string
 */
const generateUUID = () => {
  // Use crypto.randomUUID() if available (modern browsers)
  if (crypto.randomUUID) {
    return crypto.randomUUID();
  }

  // Fallback for older browsers
  return ([1e7] + -1e3 + -4e3 + -8e3 + -1e11).replace(/[018]/g, (c) =>
    (
      c ^
      (crypto.getRandomValues(new Uint8Array(1))[0] & (15 >> (c / 4)))
    ).toString(16)
  );
};

/**
 * Generates a session ID in the format: <uuidv4>-<timestampInMilliSeconds>
 * @returns {string} Session ID
 */
const generateSessionId = () => {
  const uuid = generateUUID();
  const timestamp = Date.now();
  return `${uuid}-${timestamp}`;
};

/**
 * Retrieves the session ID from sessionStorage.
 * If one doesn't exist, generates a new one.
 * @returns {string} The session ID
 */
export const getSessionId = () => {
  let sessionId = sessionStorage.getItem("orbit_session_id");

  if (!sessionId) {
    sessionId = generateSessionId();
    sessionStorage.setItem("orbit_session_id", sessionId);
  }

  return sessionId;
};

/**
 * Initializes or retrieves the session ID.
 * Only generates a new session ID if one doesn't already exist.
 * @returns {string} The session ID
 */
export const initializeSessionId = () => {
  return getSessionId();
};

/**
 * Clears the session ID from sessionStorage.
 * Should be called on logout.
 */
export const clearSessionId = () => {
  sessionStorage.removeItem("orbit_session_id");
};

/**
 * Checks if a session ID exists in sessionStorage.
 * @returns {boolean} True if session ID exists, false otherwise
 */
export const hasSessionId = () => {
  return sessionStorage.getItem("orbit_session_id") !== null;
};

/**
 * Generates a unique request ID (UUID v4)
 * Used for tracking individual API requests
 * @returns {string} A UUID v4 string
 */
export const generateRequestId = () => {
  return generateUUID();
};
